import { createFileRoute } from "@tanstack/react-router";
import {
  Sprout,
  Salad,
  ShieldCheck,
  Leaf,
  Users,
  ChefHat,
  Package,
  HeartPulse,
  Clock,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { ValueCard } from "@/components/ValueCard";
import { trackCtaClick } from "@/lib/analytics";
import founder from "@/assets/founder-portrait.jpg";
import { CmsImageBanner } from "@/components/CmsImageBanner";
import { CmsCtaLink } from "@/components/CmsCtaLink";
import { useAboutContent, cmsImage, cmsText } from "@/hooks/usePageContent";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — Poonam Kapur Healthy Kitchen" },
      {
        name: "description",
        content:
          "A socially conscious, nutrition-rich, tech-enabled food company. Meet the team and mission behind Poonam Kapur Healthy Kitchen.",
      },
      { property: "og:title", content: "About — Poonam Kapur Healthy Kitchen" },
      {
        property: "og:description",
        content:
          "Our mission, our vision, and the people curating healthy meals across Mumbai.",
      },
      { property: "og:url", content: "https://poonamkapur.com/about" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "About — Poonam Kapur Healthy Kitchen" },
      {
        name: "twitter:description",
        content:
          "Our mission, our vision, and the people curating healthy meals across Mumbai.",
      },
    ],
    links: [{ rel: "canonical", href: "https://poonamkapur.com/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About — Poonam Kapur Healthy Kitchen",
          url: "https://poonamkapur.com/about",
          about: {
            "@type": "Organization",
            name: "Poonam Kapur Healthy Kitchen",
            url: "https://poonamkapur.com",
            areaServed: "Mumbai, India",
          },
        }),
      },
    ],
  }),
  component: AboutPage,
});

const VALUES = [
  { icon: Sprout, title: "Fresh Ingredients", body: "Farm-direct produce, traceable and seasonal — never long-shelf." },
  { icon: Salad, title: "Nutrition", body: "Combos designed around balance — protein, fibre and macro-conscious portions." },
  { icon: ShieldCheck, title: "Hygiene", body: "Strict food-safety protocols across every kitchen and delivery step." },
  { icon: Leaf, title: "Sustainability", body: "Working closely with farmers, reducing waste, choosing responsible packaging." },
  { icon: Users, title: "Community", body: "Supporting farmers, women and the LGBTQIA+ community across our journey." },
];

const TRUST = [
  { icon: Sprout, title: "Farm-direct sourcing", body: "Raw materials from farmers we know and work with." },
  { icon: ChefHat, title: "Separate Veg & Non-Veg kitchens", body: "Fully separated prep spaces — no cross-contact, ever." },
  { icon: Package, title: "Food-grade packaging", body: "Hygienic, food-safe containers built for fresh in-city delivery." },
  { icon: HeartPulse, title: "Nutrition-first approach", body: "Every combo curated with a nutrition-forward intent." },
  { icon: Clock, title: "Fresh daily preparation", body: "Cooked to slot — never reheated batches sitting around." },
];

function AboutPage() {
  const cms = useAboutContent();
  return (
    <SiteLayout>
      <PageHero
        eyebrow={cmsText(cms.hero_eyebrow, "About")}
        title={cmsText(cms.hero_title, "A kitchen with a conscience.")}
        intro={cmsText(
          cms.hero_intro,
          "Poonam Kapur's Healthy Kitchen is a socially conscious, nutrition-rich, tech-enabled food company — built by a passionate team of food experts curating meals with farm-direct raw materials.",
        )}
      />

      {cmsImage(cms.hero_image_url) ? (
        <Section className="pb-0">
          <CmsImageBanner
            url={cms.hero_image_url}
            alt="About Healthy Kitchen"
            caption={cms.hero_image_caption}
          />
        </Section>
      ) : null}

      {/* Founder */}
      <Section>
        <CmsImageBanner
          url={cms.story_image_url}
          alt="Our story"
          caption={cms.story_image_caption}
          className="mb-12"
        />
        <div className="grid gap-12 md:grid-cols-[1.1fr_1fr] md:items-center md:gap-16 lg:gap-24">
          <div className="relative">
            <div
              aria-hidden
              className="absolute -bottom-4 -left-4 hidden h-full w-full rounded-3xl bg-accent/15 md:block"
            />
            <div className="relative overflow-hidden rounded-3xl ring-1 ring-border">
              <img
                src={cmsImage(cms.founder_image_url, founder)}
                alt={cmsText(
                  cms.founder_image_caption,
                  "Portrait of the founder in the kitchen.",
                )}
                loading="lazy"
                width={900}
                height={1100}
                className="aspect-[4/5] h-full w-full object-cover"
              />
              {cms.founder_image_caption ? (
                <span className="absolute bottom-4 left-4 max-w-[85%] rounded-full bg-background/90 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {cms.founder_image_caption}
                </span>
              ) : null}
            </div>
          </div>
          <div>
            <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {cmsText(cms.story_eyebrow, "Founder")}
            </span>
            <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
              {cmsText(cms.story_heading, "Food, thoughtfully made.")}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              {cmsText(
                cms.story_body,
                "Poonam Kapur's Healthy Kitchen brings together food experts, farm partners and a tech-enabled ordering flow to serve fresh, nutrition-forward meals across Mumbai — with a passionate team behind every plate.",
              )}
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {cmsText(
                cms.story_body_2,
                "Meals are prepared in hygienic, separated kitchens and delivered fresh in food-grade packaging across 70+ Mumbai neighbourhoods.",
              )}
            </p>
            {cms.founder_quote ? (
              <blockquote className="mt-6 border-l-2 border-primary/40 pl-4 text-base italic leading-relaxed text-foreground">
                “{cms.founder_quote}”
              </blockquote>
            ) : null}
            {cms.founder_name ? (
              <p className="mt-4 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{cms.founder_name}</span>
                {cms.founder_role ? ` · ${cms.founder_role}` : ""}
              </p>
            ) : null}
          </div>
        </div>
      </Section>


      {/* Mission / Vision */}
      <Section className="bg-secondary/30">
        <CmsImageBanner
          url={cms.mission_image_url}
          alt="Our mission in the kitchen"
          caption={cms.mission_image_caption}
          className="mb-12"
        />
        <div className="grid gap-12 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-primary">
              {cmsText(cms.mission_eyebrow, "Our Mission")}
            </div>
            <h2 className="mt-3 font-display text-3xl font-medium md:text-4xl">
              {cmsText(cms.mission_heading, "Healthy meals that fit real life.")}
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {cmsText(
                cms.mission_body,
                "Curate healthy, tasty and non-repetitive combos that help people achieve their health goals — without giving up on flavour or convenience.",
              )}
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-primary">Our Vision</div>
            <h2 className="mt-3 font-display text-3xl font-medium md:text-4xl">
              {cmsText(cms.vision_heading, "A community united through food.")}
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {cmsText(
                cms.vision_body,
                "A global community uniting farmers, women and the LGBTQIA+ community — across every step of the journey from farm to table.",
              )}
            </p>
          </div>
        </div>
      </Section>


      {/* Values */}
      <Section>
        <div className="max-w-2xl">
          <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {cmsText(cms.values_eyebrow, "Our Values")}
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
            {cmsText(cms.values_heading, "What we stand for.")}
          </h2>
          {cms.values_intro ? (
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              {cms.values_intro}
            </p>
          ) : null}
        </div>
        <CmsImageBanner
          url={cms.values_image_url}
          alt="What we stand for"
          caption={cms.values_image_caption}
          className="mt-10"
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {VALUES.map((v) => (
            <ValueCard key={v.title} icon={v.icon} title={v.title} body={v.body} />
          ))}
        </div>
      </Section>

      {/* Why customers trust us */}
      <Section className="bg-secondary/30">
        <div className="max-w-2xl">
          <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Why Trust Us</span>
          <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
            {cmsText(cms.trust_heading, "Why customers trust us.")}
          </h2>
          {cms.trust_intro ? (
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">{cms.trust_intro}</p>
          ) : null}
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {TRUST.map((v) => (
            <ValueCard key={v.title} icon={v.icon} title={v.title} body={v.body} />
          ))}
        </div>
      </Section>


      {/* Final CTA */}
      <Section className="pb-24">
        <CmsImageBanner
          url={cms.cta_image_url}
          alt="Healthy Kitchen meals"
          caption={cms.cta_image_caption}
          className="mb-10"
        />
        <div className="relative overflow-hidden rounded-[2rem] bg-primary px-10 py-20 text-primary-foreground md:px-16 md:py-28">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,color-mix(in_oklab,var(--accent)_35%,transparent),transparent_60%)]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,color-mix(in_oklab,var(--primary-foreground)_10%,transparent),transparent_55%)]"
          />
          <div className="relative max-w-2xl">
            <div className="mb-4 h-px w-10 bg-primary-foreground/50" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-primary-foreground/80">
              {cmsText(cms.cta_eyebrow, "Get started")}
            </span>
            <h2 className="mt-4 font-display text-4xl font-medium leading-tight md:text-6xl">
              {cmsText(cms.cta_heading, "Ready to experience healthier eating?")}
            </h2>
            <p className="mt-5 max-w-xl text-primary-foreground/90">
              {cmsText(
                cms.cta_description,
                "Explore our meal plans or get in touch — we're happy to help you find the right fit.",
              )}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <CmsCtaLink
                href={cmsText(cms.cta_primary_link, "/meal-plans")}
                onClick={() =>
                  trackCtaClick({ action: "view_meal_plans", location: "about-cta", href: "/meal-plans" })
                }
                className="inline-flex items-center rounded-full bg-accent px-6 py-3 text-sm font-medium text-accent-foreground shadow-sm transition-transform duration-300 hover:-translate-y-0.5 motion-reduce:transform-none"
              >
                {cmsText(cms.cta_primary_text, "View Meal Plans")}
              </CmsCtaLink>
              <CmsCtaLink
                href={cmsText(cms.cta_secondary_link, "/contact")}
                onClick={() =>
                  trackCtaClick({ action: "contact", location: "about-cta", href: "/contact" })
                }
                className="inline-flex items-center rounded-full border border-primary-foreground/40 bg-primary-foreground/5 px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-foreground/10"
              >
                {cmsText(cms.cta_secondary_text, "Contact Us")}
              </CmsCtaLink>
            </div>
          </div>
        </div>
      </Section>

    </SiteLayout>
  );
}